<template>
	<article role="search" class="c-list-page-layout">
		<slot
			name="header"
			v-bind="{
				filters: computedFilters,
				filterValues: computedFilterValues,
				updateValue,
			}"
		>
			<PageHeader
				v-bind="{
					...$attrs,
				}"
			>
				<slot
					name="filters"
					v-bind="{
						filters: computedFilters,
						filterValues: computedFilterValues,
						updateValue,
					}"
				></slot>
			</PageHeader>
		</slot>

		<LimboSearch
			v-slot="slotProps"
			:search-filters="filters"
			:config="{
				enableLiveSearch: true,
				allowSearchFiltersMutation: true,
				...searchConfig,
			}"
			class="relative mt-4xl"
			v-on="$listeners"
		>
			<slot name="searchContent" v-bind="slotProps">
				<TransitionGroup
					tag="div"
					mode="out-in"
					name="t-list-page-layout"
					class="relative isolate"
					style="min-height: 260px"
				>
					<!-- Results status -->
					<p
						key="total"
						role="status"
						aria-live="polite"
						class="
							mb-xl
							mt-4xl
							font-darker-grotesque font-medium
							text-h4
						"
						:class="{
							'sr-only': hideResultsStatus,
						}"
					>
						<template v-if="slotProps.state.isLoading">
							Søger efter resultater...
						</template>
						<template v-else>
							<slot name="resultsStatus" v-bind="slotProps">
								Vi har fundet
								{{
									slotProps.pagination &&
									slotProps.pagination.total
										? slotProps.pagination.total
										: 0
								}}
								resultater
							</slot>
						</template>
					</p>

					<!-- Loader -->
					<div
						v-if="slotProps.state.isLoading"
						key="loading"
						class="absolute w-full h-full pointer-events-none z-10"
					>
						<LoaderGraphic
							class="sticky w-full mb-lg"
							style="top: calc(50% - 130px)"
						/>
					</div>

					<!-- Results -->
					<div v-if="$scopedSlots.results" key="results">
						<slot name="results" v-bind="slotProps"></slot>
					</div>

					<!-- Show more -->
					<div
						v-if="
							slotProps.pagination &&
							slotProps.pagination.total >
								slotProps.pagination.limit +
									slotProps.pagination.offset
						"
						key="showmore"
						class="flex justify-center ml-auto mr-auto mt-3xl"
					>
						<BaseButton
							dark
							@click="() => slotProps.action.fetchMore()"
						>
							<template #default>
								<span v-text="'VIS FLERE'"> </span>
							</template>
							<template #icon>
								<SvgIconCaret />
							</template>
						</BaseButton>
					</div>
				</TransitionGroup>
			</slot>
		</LimboSearch>

		<slot></slot>
	</article>
</template>

<script>
import LimboSearch from '~/citi-baseline/components/LimboSearch';
import LoaderGraphic from '~/components/shared/LoaderGraphic';
import SvgIconCaret from '~/assets/svgs/icon-caret.svg?inline';

export default {
	name: 'ListPageLayout',
	components: {
		PageHeader: () => import('~/components/main/PageHeader'),
		LimboSearch,
		LoaderGraphic,
		SvgIconCaret,
	},
	inheritAttrs: false,

	props: {
		searchFilters: Object,
		searchConfig: {
			type: Object,
			default: () => ({}),
		},
		hideResultsStatus: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			filters: this.searchFilters,
		};
	},

	computed: {
		computedFilters() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = element;
			});
			return response;
		},
		computedFilterValues() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = Array.isArray(element.value)
					? element.value.find((e) => e.checked)?.value
					: element.value;
			});
			return response;
		},
	},

	methods: {
		updateValue(name, event) {
			const field = this.filters.fields.find((e) => e.name === name);
			this.isUpdatePending = true;

			if (typeof event === 'string' && Array.isArray(field.value)) {
				field.value.forEach((element) => {
					element.checked = element.value === event;
				});
			} else if (typeof event === 'string') {
				field.value = event;
			} else {
				field.value = event?.target?.value ?? '';
			}
		},
	},
};
</script>

<style lang="postcss">
.t-list-page-layout-enter-active {
	@apply duration-200 ease-smooth-out;
}
.t-list-page-layout-leave-active {
	@apply duration-500 ease-smooth-out;
}
.t-list-page-layout-enter,
.t-list-page-layout-leave-to {
	@apply opacity-0;
}
</style>
